import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import { ACCENT, SEV_COLOR, fmtDate, Field, SeverityBadge, HistoryTable } from "./shared";

export default function GcsTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    eye: 4, verbal: 5, motor: 6, intubated: false, notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const total = (Number(form.eye) || 0) + (form.intubated ? 1 : (Number(form.verbal) || 0)) + (Number(form.motor) || 0);

  async function livePreview(next) {
    try {
      const { data: r } = await api.post("/specialties/neurology/scores/gcs", {
        eye: Number(next.eye), verbal: Number(next.verbal), motor: Number(next.motor),
        intubated: !!next.intubated,
      });
      setPreview(r);
    } catch { /* ignore */ }
  }

  function set(key, val) {
    const next = { ...form, [key]: val };
    setForm(next);
    livePreview(next);
  }

  useEffect(() => { livePreview(form); /* initial preview only */ }, []);

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/specialties/neurology/gcs", {
        date: form.date, eye: Number(form.eye), verbal: Number(form.verbal),
        motor: Number(form.motor), intubated: !!form.intubated,
        notes: form.notes || null,
      });
      toast.success(`GCS ${total}${form.intubated ? "T" : ""} recorded`);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save GCS");
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-6" data-testid="neuro-gcs-tab">
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="gcs-form">
        <div className="flex items-center justify-between">
          <div className="font-heading text-lg">Score Glasgow Coma Scale</div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Total</div>
            <div className="text-3xl font-heading font-bold" style={{ color: ACCENT }} data-testid="gcs-running-total">
              {total}{form.intubated && "T"}<span className="text-sm text-[#A3ACA7]">/ 15</span>
            </div>
          </div>
        </div>

        <Field label="Assessment date">
          <input type="date" value={form.date}
                 onChange={(e) => set("date", e.target.value)}
                 data-testid="gcs-input-date"
                 className="ros-input" required />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Eye opening (E: 1–4)">
            <select value={form.eye} onChange={(e) => set("eye", e.target.value)}
                    data-testid="gcs-select-eye"
                    className="ros-input">
              <option value={4}>4 — Spontaneous</option>
              <option value={3}>3 — To verbal command</option>
              <option value={2}>2 — To pain</option>
              <option value={1}>1 — None</option>
            </select>
          </Field>
          <Field label="Verbal (V: 1–5)">
            <select value={form.verbal} onChange={(e) => set("verbal", e.target.value)}
                    data-testid="gcs-select-verbal"
                    disabled={form.intubated}
                    className="ros-input">
              <option value={5}>5 — Oriented</option>
              <option value={4}>4 — Confused</option>
              <option value={3}>3 — Inappropriate words</option>
              <option value={2}>2 — Incomprehensible sounds</option>
              <option value={1}>1 — None</option>
            </select>
          </Field>
          <Field label="Motor (M: 1–6)">
            <select value={form.motor} onChange={(e) => set("motor", e.target.value)}
                    data-testid="gcs-select-motor"
                    className="ros-input">
              <option value={6}>6 — Obeys commands</option>
              <option value={5}>5 — Localizes pain</option>
              <option value={4}>4 — Withdrawal from pain</option>
              <option value={3}>3 — Abnormal flexion (decorticate)</option>
              <option value={2}>2 — Extension (decerebrate)</option>
              <option value={1}>1 — None</option>
            </select>
          </Field>
        </div>

        <label className="inline-flex items-center gap-2 cursor-pointer text-white text-sm"
               data-testid="gcs-intubated-checkbox">
          <input type="checkbox" checked={!!form.intubated}
                 onChange={(e) => set("intubated", e.target.checked)}
                 className="accent-[#A78BFA]" />
          Patient is intubated (Verbal treated as 1; total shown with &quot;T&quot; suffix)
        </label>

        <Field label="Notes">
          <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    data-testid="gcs-input-notes"
                    className="ros-input" placeholder="Time, imaging, meds…" />
        </Field>

        {preview?.recommendation && (
          <div className="text-xs p-3 rounded-xl border"
               style={{ background: `${SEV_COLOR[preview.severity] || "#A3ACA7"}12`,
                        borderColor: `${SEV_COLOR[preview.severity] || "#A3ACA7"}40` }}
               data-testid="gcs-recommendation">
            <span className="font-semibold text-white">{preview.severity}:</span> {preview.recommendation}
          </div>
        )}

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="gcs-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="gcs-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Save GCS assessment"}
          </button>
        </div>
      </form>

      <HistoryTable
        title={`GCS history (${data.records.length})`}
        testId="gcs-history"
        rows={data.records}
        rowTestId={(r) => `gcs-row-${r.id}`}
        empty="No GCS records yet."
        columns={[
          { label: "Date", render: (r) => fmtDate(r.date) },
          { label: "Total", render: (r) => <span className="font-mono">{r.label}</span> },
          { label: "E / V / M", render: (r) => <span className="font-mono text-[#A3ACA7]">{r.eye} / {r.verbal} / {r.motor}</span> },
          { label: "Severity", render: (r) => <SeverityBadge value={r.severity} /> },
          { label: "Notes", render: (r) => <span className="text-[#A3ACA7] text-xs">{r.notes || ""}</span> },
        ]}
      />
    </div>
  );
}
